/**
 * The Documents tab — the dense document row (VALE-404).
 *
 * **Modelled line-for-line on `components/contacts/contact-row.tsx`**, which is
 * the app's established list-row grammar and the one Contacts, Inventory and the
 * balance sheet already read in:
 *
 *   `flex items-center gap-2 px-4 py-2 hover:bg-surface-subtle`
 *   → 16px file glyph · `text-sm font-medium text-foreground` name
 *   → badges · a single truncating `text-xs` muted detail line
 *   → `ml-auto` trailing slot holding the actions menu
 *
 * Where the badges sit is the LIST's call since VALE-1737 (`ListRowMarks`):
 * inline after the name, as sketched above and as the AI review's uploads
 * block keeps them, or right-aligned against the actions on the Documents
 * tab's own one-list.
 *
 * That reuse is also the answer to this tab's hardest constraint. A probate
 * matter carries eighty-plus documents and the card grid this replaces shows
 * about seven per screen; a single-line 32px row shows roughly twenty.
 *
 * Pair it with `ListSection` (`@/components/shared/page-shell`) for the grey
 * header strip and the row separators, exactly as `ContactList` does — the row
 * draws no border of its own.
 */

'use client';

import { createElement } from 'react';
import { Loader2, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import type { DocumentItem } from '@/components/documents/v2';
import { DocumentCertifiedBadge } from '@/components/documents/v2';
import { DocStateBadge } from './doc-state-badge';
import { UnreadableDocumentNotice } from '@/components/documents/core/unreadable-document-notice';
import {
  docStateOf,
  formatFileSize,
  getFileIcon,
  getFileIconColor,
  relativeTime,
  typeLabel
} from './labels';
import {
  ListRowGlyph,
  ListRowMarks,
  useListRowMarkPlacement,
  useListRowRhythm
} from '@/components/shared/list-row';
import { DocumentRowActions } from './document-row-actions';
import type { DocumentRowActionHandlers } from './document-row-actions';

/**
 * The leading slot every row reserves for its glyph, filled or empty.
 *
 * One constant rather than a size per row type, because the two have to agree
 * on pixel: the filled rows carry a file icon and an empty core slot carries a
 * dashed tile, and if those occupy different widths the document names start at
 * different x-positions and the column visibly steps in and out down the page.
 * The icons inside are centred in it, so they also share a baseline.
 *
 * **The slot is fixed while the glyph inside it scales** (VALE-1494). The glyph
 * size now comes from the enclosing surface's rhythm (`shared/list-row`), and
 * the slot deliberately does not follow it: the slot is what keeps the filename
 * column from stepping, and it is already roomy enough to centre the larger
 * glyph. It had a sibling `ROW_GLYPH_ICON` constant, removed when the rhythm
 * took over the answer — one owner, not two.
 */
export const ROW_GLYPH_SLOT = 'h-7 w-7';

export function DocumentRow({
  doc,
  isCore = false,
  /** Appended to the muted detail line — e.g. "matched on name". */
  note,
  /**
   * Re-arm a document whose analysis outran the poll window (LF-4971). On a core
   * document this reopens its typed slot dialog, which is what production's
   * Retry does; an additional document reopens the upload dialog.
   */
  onRetry,
  /**
   * Selection for the Extract bulk action (VALE-757). Both are supplied
   * together or not at all: the checkbox appears only when the surface is in a
   * position to do something with a selection, so a row on a matter with no
   * Extract handler is exactly the row it was before.
   */
  selected = false,
  onToggleSelected,
  ...actions
}: {
  doc: DocumentItem;
  isCore?: boolean;
  note?: string;
  onRetry?: () => void;
  selected?: boolean;
  onToggleSelected?: (doc: DocumentItem) => void;
} & DocumentRowActionHandlers) {
  const state = docStateOf(doc);
  const analysing = state === 'analysing';

  // One muted trailing line in `ContactRow`'s `relationshipNote` idiom — the
  // pieces worth reading at a glance, joined rather than stacked. The date lives
  // here rather than in the trailing slot, which on every other record list in
  // the app belongs to the actions menu.
  //
  // **A core row omits the type**, because its slot already is the type: the
  // death certificate row read "Death Certificate - J Citizen · Death
  // Certificate · 2.7 MB", spending the most valuable part of the line saying
  // the same word twice. An additional row keeps it, where it is the only thing
  // telling you what the document is.
  // **What the document IS leads the line, and the metadata follows it.**
  //
  // A filename often does not say what a document is — two codicils on the same
  // estate carry the same display name — so the sentence is the most valuable
  // thing here and the size is the least. Ordering matters because the line
  // `truncate`s from the right: with the note last, it was the first thing to
  // disappear on a narrow drawer or a long name, which is exactly backwards.
  //
  // `doc.notes` is already resolved backend-side (a user's note over the
  // analysis sentence), so there is one value to place and no precedence here.
  // `note` is the caller's transient annotation — "matched on name" during a
  // search — and wins while it is set, because a search result must say why it
  // matched.
  const detail = [
    note ?? doc.notes,
    isCore ? undefined : typeLabel(doc.typeSlug),
    doc.sizeBytes ? formatFileSize(doc.sizeBytes) : undefined,
    relativeTime(doc.uploadedAt)
  ]
    .filter(Boolean)
    .join(' · ');

  /* Row height, title weight and glyph size — see `shared/list-row`. */
  const rhythm = useListRowRhythm();
  const rhythmIconTile = rhythm.iconTile;

  const open = () => {
    if (!analysing) actions.onOpen?.(doc);
  };

  const marksAreInline = useListRowMarkPlacement() === 'inline';
  /*
    **This row's marks, rendered at ONE of two positions** (VALE-1737).

    Inline they sit immediately after the filename, which is where VALE-404 put
    them when this row was written and where the AI-extract review's uploads
    block keeps them. On the `column` placement they render right-aligned
    against the actions instead — the treatment VALE-1725 gave every other
    list's pills and which this row was never in the set for, now reachable so
    the Documents tab can take it.

    One node, two call sites, so the placements cannot drift.
  */
  const marks = (
    <ListRowMarks>
      {doc.isCertifiedCopy && <DocumentCertifiedBadge />}
      {/* Dense single-line row — badge only; the drawer carries the sentence. */}
      <UnreadableDocumentNotice
        reasonCode={doc.analysisFailureReasonCode}
        variant="badge"
      />
      <DocStateBadge state={state} />
    </ListRowMarks>
  );

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Open ${doc.name}`}
      aria-busy={analysing || undefined}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        }
      }}
      className={cn(
        // `group` so nothing has to be revealed on hover but the option stays
        // open; `relative` for the analysing hairline below.
        'group hover:bg-surface-subtle relative flex cursor-pointer items-center gap-2 px-4',
        rhythm.paddingY
      )}
    >
      {/*
        The checkbox sits outside the row's own click target: the row opens the
        document, and selecting one for extraction must not do that as well.
        `analysing` rows are excluded because a document mid-analysis is already
        in the batch this action would add it to.
      */}
      {!!onToggleSelected && !analysing && (
        <span
          className="flex shrink-0 items-center"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          role="presentation"
        >
          <Checkbox
            checked={selected}
            onCheckedChange={() => onToggleSelected(doc)}
            aria-label={`Select ${doc.name} for extraction`}
          />
        </span>
      )}

      {/*
        **The glyph carries the state and the row stays readable.** Veiling the
        whole row hides the filename — the one thing being scanned for — to
        report something the row can say without hiding anything. So the file
        icon becomes a spinner and the detail line says what is happening.

        A file-type glyph rather than a page thumbnail: real previews were tried
        here and removed, because at the ~24px a dense row affords they are
        unreadable, and the only size at which a page becomes recognisable costs
        about a quarter of the rows on screen — the density this whole direction
        exists to defend.
      */}
      {/*
        The tile, where the rhythm has one, replaces this row's own slot rather
        than nesting inside it (VALE-1494): both exist to reserve one fixed box
        for the glyph so the filenames start at the same x, and two boxes doing
        that would only disagree. `ListRowGlyph` renders its children bare
        without a tile, which is when the slot below is still needed.
      */}
      <ListRowGlyph>
        <span
          className={cn(
            'flex shrink-0 items-center justify-center',
            !rhythmIconTile && ROW_GLYPH_SLOT
          )}
        >
          {analysing ? (
            <Loader2
              className={cn(rhythm.icon, 'text-foreground-subtle animate-spin')}
            />
          ) : (
            createElement(getFileIcon(doc.mimeType || doc.fileType), {
              className: cn(
                rhythm.icon,
                getFileIconColor(doc.mimeType || doc.fileType)
              )
            })
          )}
        </span>
      </ListRowGlyph>

      <span
        className={cn(
          'max-w-[26rem] shrink-0 truncate',
          rhythm.titleText,
          analysing ? 'text-muted-foreground' : 'text-foreground'
        )}
      >
        {doc.name}
      </span>

      {marksAreInline && marks}

      <span
        // Both branches of the old `analysing ? 'text-slate-500' :
        // 'text-muted-foreground'` were the SAME de-emphasis rung — one spelled
        // raw, one already tokenised — so the ternary only ever varied the blue
        // cast, never the state. Collapsed rather than kept as two identical
        // arms (VALE-530 · DARK-7a, found by `dark-theme-peer-collapse.mjs`).
        className="text-muted-foreground min-w-0 truncate text-xs"
      >
        {analysing ? 'Analysing with Vale AI' : detail}
      </span>

      {!marksAreInline && marks}

      {/*
        **`ml-auto` only when the marks are inline** (VALE-1737). On the column
        placement the marks above carry the row's one pusher, and a second here
        would split the free space between them.
      */}
      <div
        className={cn(
          'flex shrink-0 items-center gap-2',
          marksAreInline && 'ml-auto'
        )}
      >
        {state === 'stalled' && (
          <>
            <span className="text-warning-subtle-foreground text-xs">
              Taking longer than expected
            </span>
            <Button
              gap="standard"
              size="2xs"
              variant="outline"
              onClick={(event) => {
                event.stopPropagation();
                onRetry?.();
              }}
            >
              <RotateCw className="h-3 w-3" />
              Retry
            </Button>
          </>
        )}

        {!analysing && (
          <DocumentRowActions doc={doc} isCore={isCore} {...actions} />
        )}
      </div>
    </div>
  );
}
